import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Cookie,
  Shield,
  Settings,
  BarChart3,
  Share2,
  Clock
} from 'lucide-react';

export default function CookiePolicy() {
  const sections = [
    {
      id: 1,
      title: "Cosa sono i Cookie",
      icon: Cookie,
      content: `
        <p>I cookie sono piccoli file di testo che i siti web salvano sul tuo dispositivo (computer, tablet o smartphone) quando li visiti.</p>
        <p>Servono per far funzionare correttamente i siti web, renderli più efficienti e fornire informazioni ai proprietari del sito.</p>
        <p>I cookie possono essere "di sessione" (vengono cancellati alla chiusura del browser) o "persistenti" (rimangono sul dispositivo per un periodo definito).</p>
      `
    },
    {
      id: 2,
      title: "Cookie Tecnici (Necessari)",
      icon: Settings,
      content: `
        <p>Questi cookie sono essenziali per il funzionamento della piattaforma e non possono essere disattivati.</p>
        <p><strong>Cookie di sessione:</strong></p>
        <ul>
          <li><code>session_id</code> - Identifica la sessione utente</li>
          <li><code>auth_token</code> - Mantiene l'autenticazione attiva</li>
          <li><code>csrf_token</code> - Protezione contro attacchi CSRF</li>
        </ul>
        <p><strong>Cookie di preferenze:</strong></p>
        <ul>
          <li><code>theme</code> - Preferenza tema chiaro/scuro</li>
          <li><code>language</code> - Preferenza lingua</li>
          <li><code>sidebar_state</code> - Stato del menu laterale</li>
        </ul>
        <p>Questi cookie non raccolgono informazioni personali e sono necessari per l'erogazione del servizio.</p>
      `
    },
    {
      id: 3,
      title: "Cookie Analitici",
      icon: BarChart3,
      content: `
        <p>Utilizziamo cookie analitici per comprendere come gli utenti interagiscono con la piattaforma, al fine di migliorare l'esperienza utente.</p>
        <p><strong>Dati raccolti:</strong></p>
        <ul>
          <li>Pagine visitate e tempo di permanenza</li>
          <li>Funzionalità più utilizzate</li>
          <li>Errori riscontrati</li>
          <li>Tipo di dispositivo e browser</li>
        </ul>
        <p>I dati analitici sono aggregati e anonimizzati. Non è possibile identificare singoli utenti dai dati raccolti.</p>
        <p><strong>Servizi utilizzati:</strong></p>
        <ul>
          <li>Analytics interno (dati conservati sui nostri server)</li>
        </ul>
        <p>Puoi disattivare i cookie analitici dalle impostazioni del tuo browser senza compromettere l'utilizzo della piattaforma.</p>
      `
    },
    {
      id: 4,
      title: "Cookie di Terze Parti",
      icon: Share2,
      content: `
        <p>La piattaforma può includere contenuti o funzionalità che utilizzano cookie di terze parti:</p>
        <p><strong>Servizi di pagamento (quando attivi):</strong></p>
        <ul>
          <li>Stripe - Per l'elaborazione sicura dei pagamenti</li>
        </ul>
        <p><strong>Autenticazione sociale (opzionale):</strong></p>
        <ul>
          <li>Google Sign-In - Per l'accesso con account Google</li>
        </ul>
        <p><strong>Servizi di Intelligenza Artificiale (chatbot):</strong></p>
        <ul>
          <li>OpenAI - Elaborazione delle risposte del chatbot AI (server USA)</li>
        </ul>
        <p><em>Il servizio API di OpenAI NON installa cookie sul tuo dispositivo e non traccia la tua navigazione. Riceve esclusivamente il testo che digiti nella chat + un identificativo di sessione anonimo. Puoi disattivare completamente il chatbot: se non lo apri, nessun dato viene trasferito a OpenAI. Per dettagli consulta la <a href="/uso-intelligenza-artificiale">pagina Uso dell'AI</a>.</em></p>
        <p>Questi servizi di terze parti hanno le proprie privacy policy e cookie policy. Ti consigliamo di consultarle per maggiori informazioni.</p>
      `
    },
    {
      id: 5,
      title: "Durata dei Cookie",
      icon: Clock,
      content: `
        <p><strong>Cookie di sessione:</strong> Vengono eliminati alla chiusura del browser.</p>
        <p><strong>Cookie persistenti:</strong></p>
        <ul>
          <li>Cookie di autenticazione: 30 giorni</li>
          <li>Cookie di preferenze: 1 anno</li>
          <li>Cookie analitici: 2 anni</li>
        </ul>
        <p>Puoi eliminare i cookie in qualsiasi momento dalle impostazioni del tuo browser.</p>
      `
    },
    {
      id: 6,
      title: "Come Gestire i Cookie",
      icon: Shield,
      content: `
        <p>Puoi controllare e/o eliminare i cookie in diversi modi:</p>
        <p><strong>Tramite il browser:</strong></p>
        <ul>
          <li><strong>Chrome:</strong> Impostazioni > Privacy e sicurezza > Cookie</li>
          <li><strong>Firefox:</strong> Impostazioni > Privacy e sicurezza > Cookie</li>
          <li><strong>Safari:</strong> Preferenze > Privacy > Cookie</li>
          <li><strong>Edge:</strong> Impostazioni > Privacy, ricerca e servizi > Cookie</li>
        </ul>
        <p><strong>Nota importante:</strong> La disattivazione dei cookie tecnici potrebbe compromettere l'utilizzo della piattaforma, impedendo l'accesso ad alcune funzionalità o rendendo impossibile l'autenticazione.</p>
        <p>Per maggiori informazioni sulla gestione dei cookie, visita <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">aboutcookies.org</a>.</p>
      `
    }
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="cookie-policy">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Cookie className="h-6 w-6" />
          Cookie Policy
        </h1>
        <p className="text-gray-500 mt-1">
          Informativa sull'utilizzo dei cookie su Assietica Gest
        </p>
        <p className="text-sm text-gray-400 mt-2">
          Ultimo aggiornamento: Marzo 2026
        </p>
      </div>

      {/* Intro */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <p className="text-blue-800">
            Questa Cookie Policy spiega come <strong>Assietica Società Cooperativa – Società Benefit</strong> utilizza 
            i cookie e tecnologie simili sulla piattaforma Assietica Gest, in conformità con il Regolamento UE 2016/679 (GDPR) 
            e la Direttiva ePrivacy 2002/58/CE.
          </p>
        </CardContent>
      </Card>

      {/* Sections */}
      {sections.map((section) => (
        <Card key={section.id}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <section.icon className="h-5 w-5 text-blue-600" />
              {section.id}. {section.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          </CardContent>
        </Card>
      ))}

      {/* Contact */}
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold mb-2">Contatti</h3>
          <p className="text-sm text-gray-600">
            Per domande sulla Cookie Policy o sull'utilizzo dei cookie, contattaci:
          </p>
          <ul className="text-sm text-gray-600 mt-2">
            <li><strong>Email:</strong> privacy@assietica.it</li>
            <li><strong>Indirizzo:</strong> Assietica Società Cooperativa - Società Benefit</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
